import { neutral } from '@bloomfieldrobotics/canopy';
import { Stack } from '@mui/material';
import { ChartsTooltipContainer } from '@mui/x-charts';
import {
  DatasetElementType,
  SeriesValueFormatter,
} from '@mui/x-charts/internals';
import { useTranslation } from '../../../../../services/translation/hooks/useTranslation';
import { getDiscreteGradientColor } from '../../../../commonUI/createDiscreteGradient';
import { BarChartTooltip } from '../../../components/ReportBarChart/components/BarChartTooltip';
import { EstimatedCountDisplay } from '../../../components/ReportBarChart/components/EstimatedCountDisplay';
import { ReportChartLegend } from '../../../components/ReportBarChart/components/ReportChartLegend';
import { StyledBarChart } from '../../../components/ReportBarChart/components/StyledBarChart';
import { useOrderedSizeBuckets } from './hooks/useOrderedSizeBuckets';
import { useSizeReportChartData } from './hooks/useSizeReportChartData';
import { SizeBucket, sizeBucketLabelMap } from './SizeBucket';

type Props = {
  scanDate: string;
  sizes: SizeBucket[];
  ranchIds: string[];
  tableLoading: boolean;
  onBarClick: (
    index: number,
    data: DatasetElementType<string | number>[]
  ) => void;
};

export const SizeReportBarChart = ({
  scanDate,
  sizes,
  ranchIds,
  tableLoading,
  onBarClick,
}: Props) => {
  const { data, yAxisValueFormatter, sizeBuckets } = useSizeReportChartData(
    scanDate,
    ranchIds,
    tableLoading
  );
  const { t } = useTranslation();

  const orderedSizeBuckets = useOrderedSizeBuckets(sizes);

  const tooltipFormatter: SeriesValueFormatter<number | null> = (
    value,
    { dataIndex }
  ) => {
    if (value === null) {
      return '';
    }

    const totalCount = data?.at(dataIndex)?.totalCount as number | undefined;
    return totalCount ? `${((value / totalCount) * 100).toFixed(1)}%` : '';
  };
  const series = orderedSizeBuckets.map((sizeBucket, index) => ({
    id: sizeBucket,
    dataKey: sizeBucket,
    label: sizeBucketLabelMap[sizeBucket as SizeBucket] || sizeBucket,
    stack: '1',
    color:
      sizeBucket === 'unknown'
        ? neutral[650]
        : getDiscreteGradientColor(
            '#66C9EB',
            '#366F8D',
            sizeBuckets.length - 1,
            index
          ),
    valueFormatter: tooltipFormatter,
  }));

  return (
    <Stack spacing={4}>
      <EstimatedCountDisplay data={data} />
      <ReportChartLegend items={series} />
      <StyledBarChart
        onItemClick={(_, barItemIdentifier) =>
          onBarClick(barItemIdentifier.dataIndex, data)
        }
        height={data?.length ? data.length * 16 : 100}
        localeText={{
          noData: t('No data matches the selected filters'),
        }}
        yAxis={[
          {
            scaleType: 'band',
            dataKey: 'dataKey',
            valueFormatter: yAxisValueFormatter,
            disableTicks: true,
            categoryGapRatio: 0.5,
            width: 80,
          },
        ]}
        slots={{
          tooltip: () => (
            <ChartsTooltipContainer>
              <BarChartTooltip totalLabel={t('Total')} />
            </ChartsTooltipContainer>
          ),
        }}
        series={series}
        dataset={data}
      />
    </Stack>
  );
};
